import React, { useEffect, useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';

const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0'];

// ==========================================
// HOOK PARA LA ANIMACIÓN DE SUSPENSO
// ==========================================
function useVotingResultsFlow(results) {
    const [phase, setPhase] = useState('animating'); // 'animating' -> 'reveal' -> 'winner'
    const [animatedData, setAnimatedData] = useState([]);
    const [winners, setWinners] = useState([]);
    const [isTie, setIsTie] = useState(false);

    const intervalRef = useRef(null);
    const revealTimeoutRef = useRef(null);
    const winnerTimeoutRef = useRef(null);

    useEffect(() => {
        if (!results || results.length === 0) return;

        // 1. Generador de datos aleatorios para el suspenso
        const generateRandomData = () => {
            return results.map(r => ({
                name: r.name,
                votes: Math.floor(Math.random() * 50) + 10 // Saltos aleatorios
            }));
        };

        setPhase('animating');
        setAnimatedData(generateRandomData());
        
        intervalRef.current = setInterval(() => {
            setAnimatedData(generateRandomData());
        }, 120);

        // 2. Revelar resultados reales a los 3 segundos
        revealTimeoutRef.current = setTimeout(() => {
            clearInterval(intervalRef.current);
            setAnimatedData(results); // Ponemos los votos reales
            setPhase('reveal');

            // 3. Declarar ganador medio segundo después
            winnerTimeoutRef.current = setTimeout(() => {
                const maxVotes = Math.max(...results.map(r => r.votes));
                
                if (maxVotes === 0) {
                    setWinners([]);
                    setIsTie(true);
                } else {
                    const topVoted = results.filter(r => r.votes === maxVotes);
                    setWinners(topVoted);
                    setIsTie(topVoted.length > 1);
                }
                
                setPhase('winner');
            }, 800);

        }, 3000);

        return () => {
            clearInterval(intervalRef.current);
            clearTimeout(revealTimeoutRef.current);
            clearTimeout(winnerTimeoutRef.current);
        };
    }, [results]);

    return { phase, animatedData, winners, isTie };
}

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
const Voting = ({ payload, results, isActiveWomensMode }) => {
    
    const { phase, animatedData, winners, isTie } = useVotingResultsFlow(results);

    // Dimensiones para el confeti
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;

    const showChart = phase === 'animating' || phase === 'reveal';
    const showWinner = phase === 'winner';

    // VISTA 1: VOTACIÓN ACTIVA (Solo pregunta y opciones)
    if (payload && !results) {
        return (
            <>
                {/* Encabezado */}
                <div className='Screen__Wait__Background'>
                    {isActiveWomensMode ? (
                        <img src='/img/selfiefan/Fondo-SelfieFan-Femenil.png' className='Screen__Wait__Background__Image' />
                    ) : isActiveWomensMode === false && (
                        <img src='/img/selfiefan/Fondo-SelfieFan-Varonil.png' className='Screen__Wait__Background__Image' />
                    )}
                </div>

                {/* <div className='QuizzWithImageAndOptions__Screen'>
                    <div className="QuizzWithImageAndOptions__Screen__Top" style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '6rem' }}>{payload.question}</p>
                        
                        <div className="d-flex justify-content-center gap-5 mt-5">
                            {payload.options.map((opt, idx) => (
                                <div key={idx} className="bg-white text-dark fw-bold rounded px-5 py-4 shadow" style={{ fontSize: '4rem' }}>
                                    {opt.text}
                                </div>
                            ))}
                        </div>

                        <img src="/img/page/qr-inicio-fdm-gris.png" className="Screen__Welcome__Container__QR__Image" alt="Logo" />

                        <p className="text-white text-center mt-5 fs-2 fw-bold">¡Vota ahora desde tu celular!</p>
                    </div>
                </div> */}

                <section className="Screen__Welcome__Container Container-FullHeight">
                    <div className='Screen__Welcome__Container__Info'>
                        <img src="/img/page/adsl-five.png" className="Screen__Welcome__Container__Info__Image" alt="Logo" style={{ width: '450px' }}  />
                        <p className='Screen__Welcome__Container__Text' style={{ fontSize: '6.6em' }}>{payload.question}</p>
                        {payload.options.map((opt, idx) => (
                            // <div key={idx} className="bg-white text-dark fw-bold rounded px-5 py-4 shadow" style={{ fontSize: '4rem' }}>
                            <div key={idx} className='Screen__Welcome__Container__Text mt-4' style={{ fontSize: '5em', border: '1px dashed #fff', borderRadius: '8px', marginBottom: '20px', marginRight: '10px' }}>
                                {opt.text}
                            </div>
                        ))}
                        {/* <p className='Screen__Welcome__Container__Text'></p>
                        <p className='Screen__Welcome__Container__Text Screen__Welcome__Container__Text--Highlight'>WWW.ADSL.MX</p> */}
                    </div>

                    <div className='Screen__Welcome__Container__QR'>
                        <img src="/img/page/qr-inicio-fdm-gris.png" className="Screen__Welcome__Container__QR__Image" alt="Logo" style={{ width: '750px' }} />
                    </div>
                </section>

                {/* Pie de página */}
                <footer className='Screen__Wait__Footer'>
                    <p className="text-center FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
                </footer>
            </>
        );
    }

    // VISTA 2: RESULTADOS (Con animación y ganador)
    if (results) {
        return (
            <>
                <div className='QuizzWithImageAndOptions__Screen' style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
                    {showWinner && !isTie && <Confetti width={width} height={height} recycle={false} numberOfPieces={400} />}
                    
                    <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center p-5">
                        
                        {/* FASE A: MUESTRA LA GRÁFICA EN SUSPENSO */}
                        {showChart && (
                            <>
                                {/* <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '4.5rem' }}>Resultados Finales</p> */}
                                <div style={{ height: '30rem', width: '80%', backgroundColor: 'transparent' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={animatedData}>
                                            <CartesianGrid stroke="#3a3a3a" strokeDasharray="3 3" />
                                            <XAxis dataKey="name" tick={{ fill: '#eaeaea', fontSize: '2.5rem' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} />
                                            
                                            {/* YAxis sin números ni líneas para que no se vea el puntaje mientras baila */}
                                            <YAxis tick={false} axisLine={false} tickLine={false} />
                                            
                                            <Bar dataKey="votes" isAnimationActive animationDuration={400} fillOpacity={0.95}>
                                                {animatedData.map((entry, idx) => (
                                                    <Cell key={`cell-${idx}`} fill={barColors[idx % barColors.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </>
                        )}

                        {/* FASE B: OCULTA LA GRÁFICA Y MUESTRA SOLO AL GANADOR */}
                        {showWinner && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1.12 }}
                                transition={{ duration: 0.6, type: 'spring' }}
                                className="text-center mt-5"
                            >
                                <p><i className="bi bi-trophy text-warning" style={{ fontSize: '10em', marginBottom: '0', paddingBottom: '0' }}></i></p>
                                
                                {isTie ? (
                                    <p className="text-white fw-bold mt-4" style={{ fontSize: '10em', textShadow: '2px 2px 4px #000' }}>
                                        ¡Es un empate!
                                    </p>
                                ) : (
                                    <p className="text-white fw-bold mt-4" style={{ fontSize: '10em', textShadow: '2px 2px 4px #000' }}>
                                        <span className="text-warning">Ganó {winners[0]?.name}</span>
                                    </p>
                                )}
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Pie de página */}
                <footer className='Screen__Wait__Footer'>
                    <p className="text-center FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
                </footer>
            </>
        );
    }

    return null;
}

export { Voting };









// import React, { useEffect, useState, useRef } from 'react';
// import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
// import { motion } from 'framer-motion';
// import Confetti from 'react-confetti';

// const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0'];

// // ==========================================
// // HOOK PARA LA ANIMACIÓN DE SUSPENSO
// // ==========================================
// function useVotingResultsFlow(results) {
//     const [phase, setPhase] = useState('animating'); // 'animating' -> 'reveal' -> 'winner'
//     const [animatedData, setAnimatedData] = useState([]);
//     const [winners, setWinners] = useState([]);
//     const [isTie, setIsTie] = useState(false);

//     const intervalRef = useRef(null);
//     const revealTimeoutRef = useRef(null);
//     const winnerTimeoutRef = useRef(null);

//     useEffect(() => {
//         if (!results || results.length === 0) return;

//         // 1. Generador de datos aleatorios para el suspenso
//         const generateRandomData = () => {
//             return results.map(r => ({
//                 name: r.name,
//                 votes: Math.floor(Math.random() * 50) + 10 // Saltos aleatorios
//             }));
//         };

//         setPhase('animating');
//         setAnimatedData(generateRandomData());
        
//         intervalRef.current = setInterval(() => {
//             setAnimatedData(generateRandomData());
//         }, 120);

//         // 2. Revelar resultados reales a los 3 segundos
//         revealTimeoutRef.current = setTimeout(() => {
//             clearInterval(intervalRef.current);
//             setAnimatedData(results); // Ponemos los votos reales
//             setPhase('reveal');

//             // 3. Declarar ganador medio segundo después
//             winnerTimeoutRef.current = setTimeout(() => {
//                 const maxVotes = Math.max(...results.map(r => r.votes));
                
//                 if (maxVotes === 0) {
//                     setWinners([]);
//                     setIsTie(true);
//                 } else {
//                     const topVoted = results.filter(r => r.votes === maxVotes);
//                     setWinners(topVoted);
//                     setIsTie(topVoted.length > 1);
//                 }
                
//                 setPhase('winner');
//             }, 500);

//         }, 3000);

//         return () => {
//             clearInterval(intervalRef.current);
//             clearTimeout(revealTimeoutRef.current);
//             clearTimeout(winnerTimeoutRef.current);
//         };
//     }, [results]);

//     return { phase, animatedData, winners, isTie };
// }

// // ==========================================
// // COMPONENTE PRINCIPAL
// // ==========================================
// const Voting = ({ payload, results }) => {
    
//     const { phase, animatedData, winners, isTie } = useVotingResultsFlow(results);

//     // Dimensiones para el confeti
//     const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
//     const height = typeof window !== 'undefined' ? window.innerHeight : 768;

//     // VISTA 1: VOTACIÓN ACTIVA (Solo pregunta y opciones)
//     if (payload && !results) {
//         return (
//             <div className='QuizzWithImageAndOptions__Screen'>
//                 <div className="QuizzWithImageAndOptions__Screen__Top" style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                     <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '6rem' }}>{payload.question}</p>
                    
//                     <div className="d-flex justify-content-center gap-5 mt-5">
//                         {payload.options.map((opt, idx) => (
//                             <div key={idx} className="bg-white text-dark fw-bold rounded px-5 py-4 shadow" style={{ fontSize: '4rem' }}>
//                                 {opt.text}
//                             </div>
//                         ))}
//                     </div>
//                     <p className="text-white text-center mt-5 fs-2 fw-bold" style={{ textShadow: '2px 2px 4px #000' }}>¡Vota ahora desde tu celular!</p>
//                 </div>
//             </div>
//         );
//     }

//     // VISTA 2: RESULTADOS (Con animación)
//     if (results) {
//         const showWinner = phase === 'winner';

//         return (
//             <div className='QuizzWithImageAndOptions__Screen' style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
//                 {showWinner && !isTie && <Confetti width={width} height={height} recycle={false} numberOfPieces={400} />}
                
//                 <div className="w-100 h-100 d-flex flex-column justify-content-center p-5">
//                     <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '4.5rem' }}>Resultados Finales</p>
                    
//                     <div style={{ height: '24rem', width: '80%', margin: '0 auto', backgroundColor: 'transparent' }}>
//                         <ResponsiveContainer width="100%" height="100%" aspect={4}>
//                             <BarChart data={animatedData}>
//                                 <CartesianGrid stroke="#3a3a3a" strokeDasharray="3 3" />
//                                 <XAxis dataKey="name" tick={{ fill: '#eaeaea', fontSize: '1.5rem' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} />
//                                 <YAxis tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} />
//                                 <Tooltip contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }} />
                                
//                                 <Bar dataKey="votes" isAnimationActive animationDuration={400} fillOpacity={0.95}>
//                                     {animatedData.map((entry, idx) => (
//                                         <Cell key={`cell-${idx}`} fill={barColors[idx % barColors.length]} />
//                                     ))}
//                                 </Bar>
//                             </BarChart>
//                         </ResponsiveContainer>
//                     </div>

//                     {/* TEXTO DE GANADOR O EMPATE */}
//                     {showWinner && (
//                         <motion.div
//                             initial={{ scale: 0 }}
//                             animate={{ scale: 1.12 }}
//                             transition={{ duration: 0.6, type: 'spring' }}
//                             className="text-center mt-5"
//                         >
//                             <p><i className="bi bi-trophy text-warning" style={{ fontSize: '4rem' }}></i></p>
                            
//                             {isTie ? (
//                                 <p className="text-white fw-bold" style={{ fontSize: '3rem', textShadow: '2px 2px 4px #000' }}>
//                                     ¡Es un empate!
//                                 </p>
//                             ) : (
//                                 <p className="text-white fw-bold" style={{ fontSize: '3.5rem', textShadow: '2px 2px 4px #000' }}>
//                                     El ganador es:<br />
//                                     <span className="text-warning">{winners[0]?.name}</span>
//                                 </p>
//                             )}
//                         </motion.div>
//                     )}
//                 </div>
//             </div>
//         );
//     }

//     return null;
// }

// export { Voting };









// import React from 'react';
// import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';

// const barColors = ['#f44336', '#2196f3']; // Rojo y Azul

// const Voting = ({ payload, results }) => {
    
//     // Vista 1: La pregunta en pantalla (cuando inicia la votación)
//     if (payload && !results) {
//         return (
//             <div className='QuizzWithImageAndOptions__Screen'>
//                 <div className="QuizzWithImageAndOptions__Screen__Top" style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                     <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '6rem' }}>{payload.question}</p>
                    
//                     <div className="d-flex justify-content-center gap-5 mt-5">
//                         {payload.options.map((opt, idx) => (
//                             <div key={idx} className="bg-white text-dark fw-bold rounded px-5 py-4 shadow" style={{ fontSize: '4rem' }}>
//                                 {opt.text}
//                             </div>
//                         ))}
//                     </div>
//                     <p className="text-white text-center mt-5 fs-2">¡Vota ahora desde tu celular!</p>
//                 </div>
//             </div>
//         );
//     }

//     // Vista 2: Los Resultados Oficiales
//     if (results) {
//         return (
//             <div className='QuizzWithImageAndOptions__Screen'>
//                 <div className="QuizzWithImageAndOptions__Screen__Top w-100 h-100 d-flex flex-column justify-content-center p-5">
//                     <p className='QuizzWithImageAndOptions__Screen__Title mb-5' style={{ fontSize: '4.5rem' }}>Resultados Finales</p>
                    
//                     <div style={{ height: '45vh', width: '80%', margin: '0 auto', backgroundColor: 'rgba(0,0,0,0.6)', padding: '2rem', borderRadius: '1rem' }}>
//                         <ResponsiveContainer width="100%" height="100%">
//                             <BarChart data={results}>
//                                 <CartesianGrid stroke="#3a3a3a" strokeDasharray="3 3" />
//                                 <XAxis dataKey="name" tick={{ fill: '#fff', fontSize: '2rem' }} />
//                                 <YAxis tick={{ fill: '#fff', fontSize: '1.5rem' }} />
//                                 <Tooltip />
//                                 <Bar dataKey="votes" isAnimationActive animationDuration={800}>
//                                     {results.map((entry, index) => (
//                                         <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
//                                     ))}
//                                 </Bar>
//                             </BarChart>
//                         </ResponsiveContainer>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     return null;
// }

// export { Voting };